// src/pages/creche/CrecheHomePage.jsx

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Avatar
} from '@mui/material';
import {
  ChildCare,
  AccessTime,
  LocalHospital,
  Assessment,
  CheckCircle,
  Security,
  Notifications,
  CalendarMonth,
  Favorite,
  School,
  TrendingUp,
  ExpandMore,
  ArrowForward,
  VerifiedUser,
  Groups,
  AttachMoney
} from '@mui/icons-material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const CrecheHomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Funcionalidades principais
  const features = [
    {
      icon: <AccessTime sx={{ fontSize: 50 }} />,
      title: 'Check-ins Diários',
      description: 'Registo de entradas e saídas com horários precisos e notificações automáticas aos responsáveis.',
      color: '#1976d2'
    },
    {
      icon: <CheckCircle sx={{ fontSize: 50 }} />,
      title: 'Controlo de Presenças',
      description: 'Marcação de presença diária, faltas justificadas e relatórios de assiduidade por criança.',
      color: '#2e7d32'
    },
    {
      icon: <LocalHospital sx={{ fontSize: 50 }} />,
      title: 'Fichas de Saúde',
      description: 'Registo de alergias, medicação, vacinas e ocorrências médicas com histórico completo.',
      color: '#d32f2f'
    },
    {
      icon: <Assessment sx={{ fontSize: 50 }} />,
      title: 'Relatórios Detalhados',
      description: 'Estatísticas de ocupação, assiduidade, receitas e análises operacionais em tempo real.',
      color: '#0288d1'
    }
  ];

  // Benefícios para pais
  const beneficiosPais = [
    {
      icon: <Notifications />,
      title: 'Notificações em Tempo Real',
      description: 'Receba alertas de entrada, saída e ocorrências importantes.'
    },
    {
      icon: <Security />,
      title: 'Segurança e Transparência',
      description: 'Acompanhe o dia do seu filho com total confiança e visibilidade.'
    },
    {
      icon: <CalendarMonth />,
      title: 'Calendário de Actividades',
      description: 'Acesso aos eventos, atividades programadas e comunicados.'
    },
    {
      icon: <Favorite />,
      title: 'Portal do Responsável',
      description: 'Área exclusiva com histórico completo e documentos da criança.'
    }
  ];

  // Benefícios para instituições
  const beneficiosInstituicoes = [
    {
      icon: <TrendingUp />,
      title: 'Gestão Eficiente',
      description: 'Automatize processos e reduza tempo administrativo em 70%.'
    },
    {
      icon: <Groups />,
      title: 'Organização por Grupos',
      description: 'Berçário, Infantil I e II com capacidades e rotinas personalizadas.'
    },
    {
      icon: <AttachMoney />,
      title: 'Controlo Financeiro',
      description: 'Gestão de matrículas, prestações e pagamentos integrada.'
    },
    {
      icon: <VerifiedUser />,
      title: 'Conformidade Legal',
      description: 'Registos auditáveis e relatórios para entidades reguladoras.'
    }
  ];

  // Grupos etários
  const grupos = [
    {
      nome: 'Berçário',
      idade: '0-2 anos',
      icon: '👶',
      descricao: 'Cuidados especializados para bebés com rotinas adaptadas.',
      color: '#f093fb'
    },
    {
      nome: 'Infantil I',
      idade: '2-4 anos',
      icon: '🧒',
      descricao: 'Estimulação cognitiva e desenvolvimento motor.',
      color: '#667eea'
    },
    {
      nome: 'Infantil II',
      idade: '4-6 anos',
      icon: '👧',
      descricao: 'Preparação pré-escolar e socialização.',
      color: '#764ba2'
    }
  ];

  return (
    <Box>
     {/* Slider Hero Creche */}
<Box sx={{ position: 'relative' }}>
  <Slider
    dots={true}
    infinite={true}
    speed={600}
    slidesToShow={1}
    slidesToScroll={1}
    autoplay={true}
    autoplaySpeed={5000}
    arrows={!isMobile}
    fade={true}
  >
    {/* Slide 1: Boas-vindas */}
    <Box>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          minHeight: { xs: '60vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url(/images/pattern-creche.svg)',
            opacity: 0.1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ChildCare sx={{ fontSize: { xs: 40, md: 60 }, mr: 2 }} />
                  <Typography variant={isMobile ? 'h4' : 'h2'} sx={{ fontWeight: 800 }}>
                    Gestão de Creche
                  </Typography>
                </Box>
                <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ mb: 3, fontWeight: 300, opacity: 0.95 }}>
                  Plataforma completa para educação infantil com segurança, transparência e carinho
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    component={RouterLink}
                    to="/pre-inscricao"
                    endIcon={<ArrowForward />}
                    sx={{
                      bgcolor: 'white',
                      color: '#667eea',
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)', transform: 'translateY(-2px)' }
                    }}
                  >
                    Fazer Pré-Inscrição
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component={RouterLink}
                    to="/leads"
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      fontWeight: 700,
                      px: 4,
                      '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    Solicitar Demo
                  </Button>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  height: { xs: 250, md: 400 },
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed rgba(255,255,255,0.3)'
                }}
              >
              <Box
  component="img"
  src="/images/creche-hero.png"
  alt="Gestão de Creche"
  sx={{
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 4
  }}
/>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>

    {/* Slide 2: Check-ins em Tempo Real */}
    <Box>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          minHeight: { xs: '60vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative'
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: { xs: 250, md: 400 },
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed rgba(255,255,255,0.3)'
                }}
              >
              <Box
  component="img"
  src="/images/creche-checkin.png"
  alt="Check-ins em Tempo Real"
  sx={{
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 4
  }}
/>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ color: 'white', textAlign: { xs: 'center', md: 'left' } }}>
                <AccessTime sx={{ fontSize: { xs: 50, md: 70 }, mb: 2 }} />
                <Typography variant={isMobile ? 'h4' : 'h3'} gutterBottom sx={{ fontWeight: 800 }}>
                  Check-ins em Tempo Real
                </Typography>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 3, fontWeight: 300, opacity: 0.95 }}>
                  Notificações instantâneas de entrada e saída para total tranquilidade dos pais
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  component={RouterLink}
                  to="/pre-inscricao"
                  sx={{
                    bgcolor: 'white',
                    color: '#4facfe',
                    fontWeight: 700,
                    px: 4,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                  }}
                >
                  Conhecer Mais
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>

    {/* Slide 3: Saúde e Bem-Estar */}
    <Box>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          minHeight: { xs: '60vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative'
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ color: 'white' }}>
                <LocalHospital sx={{ fontSize: { xs: 50, md: 70 }, mb: 2 }} />
                <Typography variant={isMobile ? 'h4' : 'h3'} gutterBottom sx={{ fontWeight: 800 }}>
                  Saúde e Bem-Estar
                </Typography>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 3, fontWeight: 300, opacity: 0.95 }}>
                  Fichas médicas completas, alergias, medicação e histórico sempre acessível
                </Typography>
                <Stack spacing={2} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle />
                    <Typography variant="h6">Registo de vacinas e medicação</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle />
                    <Typography variant="h6">Alergias e restrições alimentares</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle />
                    <Typography variant="h6">Contactos de emergência</Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  size="large"
                  component={RouterLink}
                  to="/pre-inscricao"
                  sx={{
                    bgcolor: 'white',
                    color: '#f5576c',
                    fontWeight: 700,
                    px: 4,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                  }}
                >
                  Inscrever Agora
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  height: { xs: 250, md: 400 },
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed rgba(255,255,255,0.3)'
                }}
              >
               <Box
  component="img"
  src="/images/creche-saude.png"
  alt="Saúde e Bem-Estar"
  sx={{
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 4
  }}
/>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>

    {/* Slide 4: Grupos Etários */}
    <Box>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          minHeight: { xs: '60vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative'
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ color: 'white', textAlign: 'center', mb: 4 }}>
            <Typography variant={isMobile ? 'h4' : 'h2'} gutterBottom sx={{ fontWeight: 800 }}>
              Grupos Especializados por Idade
            </Typography>
            <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 4, fontWeight: 300, opacity: 0.95 }}>
              Cuidados adaptados a cada fase do desenvolvimento
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'rgba(255,255,255,0.95)', height: '100%', textAlign: 'center', p: 3 }}>
                <Typography variant="h1" sx={{ mb: 2 }}>👶</Typography>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#fa709a' }}>
                  Berçário
                </Typography>
                <Chip label="0-2 anos" sx={{ mb: 2, bgcolor: '#fa709a', color: 'white', fontWeight: 600 }} />
                <Typography variant="body1" color="text.secondary">
                  Cuidados especializados para bebés com rotinas adaptadas
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'rgba(255,255,255,0.95)', height: '100%', textAlign: 'center', p: 3 }}>
                <Typography variant="h1" sx={{ mb: 2 }}>🧒</Typography>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#667eea' }}>
                  Infantil I
                </Typography>
                <Chip label="2-4 anos" sx={{ mb: 2, bgcolor: '#667eea', color: 'white', fontWeight: 600 }} />
                <Typography variant="body1" color="text.secondary">
                  Estimulação cognitiva e desenvolvimento motor
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'rgba(255,255,255,0.95)', height: '100%', textAlign: 'center', p: 3 }}>
                <Typography variant="h1" sx={{ mb: 2 }}>👧</Typography>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#764ba2' }}>
                  Infantil II
                </Typography>
                <Chip label="4-6 anos" sx={{ mb: 2, bgcolor: '#764ba2', color: 'white', fontWeight: 600 }} />
                <Typography variant="body1" color="text.secondary">
                  Preparação pré-escolar e socialização
                </Typography>
              </Card>
            </Grid>
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/pre-inscricao"
              sx={{
                bgcolor: 'white',
                color: '#fa709a',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
              }}
            >
              Escolher Grupo
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  </Slider>
</Box>
      {/* Funcionalidades Principais */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{ fontWeight: 700, mb: 1 }}
        >
          Funcionalidades Principais
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, fontWeight: 300 }}
        >
          Tudo o que precisa para gerir a sua creche com eficiência e segurança
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: feature.color,
                      width: 80,
                      height: 80,
                      margin: '0 auto 16px',
                      boxShadow: 3
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Para Pais e Responsáveis */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 400,
                  borderRadius: 4,
                  bgcolor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #ddd',
                  boxShadow: 2
                }}
              >
              <Box
  component="img"
  src="/images/creche-portal-responsavel.png"
  alt="Portal do Responsável"
  sx={{
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 4,
    boxShadow: 3
  }}
/>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                Para Pais e Responsáveis
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4, fontWeight: 300 }}>
                Acompanhe o dia do seu filho com total transparência
              </Typography>

              <Stack spacing={3}>
                {beneficiosPais.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                      {item.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Para Instituições */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
              Para Instituições de Ensino
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4, fontWeight: 300 }}>
              Gestão completa, eficiente e conforme
            </Typography>

            <Stack spacing={3}>
              {beneficiosInstituicoes.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.main' }}>
                    {item.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: 400,
                borderRadius: 4,
                bgcolor: 'grey.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #ddd',
                boxShadow: 2
              }}
            >
            <Box
  component="img"
  src="/images/creche-dashboard-admin.png"
  alt="Dashboard Administrativo"
  sx={{
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 4,
    boxShadow: 3
  }}
/>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Grupos Etários */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700 }}>
            Grupos Etários
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, fontWeight: 300 }}
          >
            Cuidados especializados para cada fase do desenvolvimento
          </Typography>

          <Grid container spacing={4}>
            {grupos.map((grupo, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    background: `linear-gradient(135deg, ${grupo.color}15, ${grupo.color}08)`,
                    border: `2px solid ${grupo.color}`,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h1" sx={{ mb: 2 }}>
                      {grupo.icon}
                    </Typography>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                      {grupo.nome}
                    </Typography>
                    <Chip
                      label={grupo.idade}
                      sx={{
                        mb: 2,
                        bgcolor: grupo.color,
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      {grupo.descricao}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700 }}>
          Perguntas Frequentes
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, fontWeight: 300 }}
        >
          Esclareça as suas dúvidas sobre o módulo de Creche
        </Typography>

        <Stack spacing={2}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Como funciona o check-in diário?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                O check-in é registado pela equipa da creche através do sistema. Os responsáveis recebem notificação automática no momento da entrada e saída da criança, com horário preciso e identificação do funcionário responsável.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Como acedo ao Portal do Responsável?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Após a matrícula, receberá credenciais de acesso por email. No portal, pode consultar presenças, fichas de saúde, comunicados, calendário de atividades e fazer downloads de documentos.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Que informações de saúde são registadas?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Registamos alergias, intolerâncias alimentares, medicação regular, cartão de vacinação, tipo sanguíneo, contactos de emergência médica e histórico de ocorrências. Todos os dados são protegidos conforme a Lei de Protecção de Dados.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Como funciona o processo de pré-inscrição?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                A pré-inscrição é feita online em 4 passos simples: dados da criança, dados do responsável, escolha do grupo etário e pagamento. Após confirmação do pagamento, a matrícula é activada automaticamente e recebe acesso ao portal.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Container>

      {/* CTA Final */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 }
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 800 }}>
            Pronto para Transformar a Gestão da Sua Creche?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
            Junte-se às instituições que já confiam no ZenKroll para educação infantil
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/pre-inscricao"
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              Fazer Pré-Inscrição
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/leads"
              sx={{
                borderColor: 'white',
                color: 'white',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Solicitar Demonstração
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default CrecheHomePage;